import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaSync } from "react-icons/fa";
import deviceRegistrationService from "../services/deviceRegistrationService";
import locationService from "../services/locationService";
import deviceGroupService from "../services/deviceGroupService";
import organizationSettingsService from "../services/organizationSettingsService";
import isAdministrator, { isStaff } from "../utilities/checkPrivileges";
import AssignUserModal from "../components/modals/AssignUserModal";
import { expiryBadgeClass, expiryLabel } from "../utils/tokenExpiry";
import { continueConfiguredSetup } from "../services/enrollService";

const DeviceRegistrations = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [registrations, setRegistrations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [locationFilter, setLocationFilter] = useState("");
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("");
  const [depStatusFilter, setDepStatusFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formData, setFormData] = useState({
    serialNumber: "",
    plannedDeviceName: "",
    assetTag: "",
    location: "",
    groups: [],
    notes: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [depAccountInfo, setDepAccountInfo] = useState(null);
  const [depAccountError, setDepAccountError] = useState(null);
  const [syncingDep, setSyncingDep] = useState(false);
  const [orgTokens, setOrgTokens] = useState(null);
  const [depDevices, setDepDevices] = useState({});
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [depProfiles, setDepProfiles] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignProfileUuid, setAssignProfileUuid] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [, setShowAddProfile] = useState(false);
  const [newProfileUuid, setNewProfileUuid] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignUserTarget, setAssignUserTarget] = useState(null);
  const admin = isAdministrator();
  const staff = isStaff();

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const loadRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await deviceRegistrationService.getDeviceRegistrations(statusFilter);
      setRegistrations(data);
      if (data.length > 0) {
        const serials = data.map((r) => r.serialNumber);
        deviceRegistrationService.getDepDeviceDetails(serials)
          .then((res) => setDepDevices(res.devices || {}))
          .catch(() => setDepDevices({}));
      } else {
        setDepDevices({});
      }
    } catch (err) {
      toast.error("Registrierungen konnten nicht geladen werden.");
      setRegistrations([]);
      setDepDevices({});
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  useEffect(() => {
    locationService.getLocations().then(setLocations).catch(() => setLocations([]));
    deviceGroupService.getDeviceGroups().then(setGroups).catch(() => setGroups([]));
    deviceRegistrationService.getDepAccountInfo()
      .then(setDepAccountInfo)
      .catch((err) => setDepAccountError(err.response?.data?.message || err.message || "DEP nicht erreichbar"));
    if (isAdministrator()) {
      organizationSettingsService.getSettings()
        .then(setOrgTokens)
        .catch(() => setOrgTokens(null));
    }
    deviceRegistrationService.getDepProfiles().then(setDepProfiles).catch(() => setDepProfiles([]));
  }, []);

  const loadDepAccount = () => {
    deviceRegistrationService.getDepAccountInfo()
      .then((data) => {
        setDepAccountInfo(data);
        setDepAccountError(null);
      })
      .catch((err) => setDepAccountError(err.response?.data?.message || err.message || "DEP nicht erreichbar"));
  };

  const handleSyncFromApple = async () => {
    setSyncingDep(true);
    try {
      const result = await deviceRegistrationService.syncDepDevices();
      const parts = [
        result.created ? `${result.created} neu` : null,
        result.updated ? `${result.updated} aktualisiert` : null,
        result.deleted ? `${result.deleted} entfernt` : null,
      ].filter(Boolean);
      toast.success(parts.length ? `Apple-Sync: ${parts.join(", ")}.` : "Apple-Sync abgeschlossen. Keine Änderungen.");
      await loadRegistrations();
      loadDepAccount();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Sync mit Apple fehlgeschlagen.");
    } finally {
      setSyncingDep(false);
    }
  };

  const lastAppleSyncLabel = (() => {
    const fromAccount = depAccountInfo?.dep_last_sync_at;
    const fromDevices = registrations
      .map((r) => r.depLastSync)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
    const raw = fromAccount || fromDevices;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serialNumber.trim()) {
      toast.error("Seriennummer ist erforderlich.");
      return;
    }

    const payload = {
      serialNumber: formData.serialNumber.trim(),
      plannedDeviceName: formData.plannedDeviceName.trim() || undefined,
      assetTag: formData.assetTag.trim() || undefined,
      location: formData.location || undefined,
      groups: Array.isArray(formData.groups) ? formData.groups.filter(Boolean) : undefined,
      notes: formData.notes.trim() || undefined
    };

    try {
      if (editingId) {
        await deviceRegistrationService.updateDeviceRegistration(editingId, payload);
        toast.success("Registrierung aktualisiert.");
        handleCloseEditModal();
      } else {
        await deviceRegistrationService.createDeviceRegistration(payload);
        toast.success("Registrierung angelegt.");
        handleCloseAddModal();
      }
      resetForm();
      loadRegistrations();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Fehler";
      toast.error(msg);
    }
  };

  const resetForm = () => {
    setFormData({
      serialNumber: "",
      plannedDeviceName: "",
      assetTag: "",
      location: "",
      groups: [],
      notes: ""
    });
    setEditingId(null);
  };

  const handleOpenEditModal = (reg) => {
    const groupIds = (reg.groups || (reg.group ? [reg.group] : [])).map((g) => (typeof g === "object" ? g?._id : g)).filter(Boolean);
    setFormData({
      serialNumber: reg.serialNumber,
      plannedDeviceName: reg.plannedDeviceName || "",
      assetTag: reg.assetTag || "",
      location: reg.location?._id || "",
      groups: groupIds,
      notes: reg.notes || ""
    });
    setEditingId(reg._id);
    setEditTarget(reg);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditTarget(null);
    resetForm();
  };

  const handleOpenAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const handleShowProfile = async (profileUuid) => {
    if (!profileUuid) return;
    setShowProfileModal(true);
    setProfileData(null);
    setProfileError(null);
    setProfileLoading(true);
    try {
      const data = await deviceRegistrationService.getDepProfile(profileUuid);
      setProfileData(data);
    } catch (err) {
      setProfileError(err.response?.data?.message || err.message || "Profil konnte nicht geladen werden.");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    setProfileData(null);
    setProfileError(null);
  };

  const handleOpenAssignModal = (regOrRegs) => {
    setAssignTarget(Array.isArray(regOrRegs) ? regOrRegs : [regOrRegs]);
    setAssignProfileUuid("");
    setShowAddProfile(false);
    setNewProfileUuid("");
    setNewProfileName("");
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssignTarget(null);
    setAssignProfileUuid("");
    setAssignLoading(false);
    setShowAddProfile(false);
  };

  const handleAddDepProfile = async () => {
    if (!newProfileUuid.trim()) {
      toast.error("Profile UUID ist erforderlich.");
      return;
    }
    try {
      const created = await deviceRegistrationService.createDepProfile(newProfileUuid.trim(), newProfileName.trim() || undefined);
      setDepProfiles((prev) => [...prev, created]);
      setAssignProfileUuid(created.profile_uuid);
      setShowAddProfile(false);
      setNewProfileUuid("");
      setNewProfileName("");
      toast.success("Profil hinzugefügt.");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Profil konnte nicht hinzugefügt werden.");
    }
  };

  const handleAssignProfile = async () => {
    const targets = Array.isArray(assignTarget) ? assignTarget : (assignTarget ? [assignTarget] : []);
    if (targets.length === 0 || !assignProfileUuid) {
      toast.error("Bitte ein Profil auswählen.");
      return;
    }
    const serialNumbers = targets.map((t) => t.serialNumber).filter(Boolean);
    if (serialNumbers.length === 0) {
      toast.error("Keine gültigen Geräte ausgewählt.");
      return;
    }
    setAssignLoading(true);
    try {
      const res = await deviceRegistrationService.assignDepProfileToDevices(assignProfileUuid, serialNumbers);
      const deviceResults = res.devices || {};
      const updatedSerials = Object.keys(deviceResults).filter((sn) => deviceResults[sn] === "SUCCESS");
      if (updatedSerials.length > 0) {
        const fresh = await deviceRegistrationService.getDepDeviceDetails(updatedSerials);
        setDepDevices((prev) => ({ ...prev, ...(fresh.devices || {}) }));
        toast.success(`Profil zugewiesen: ${updatedSerials.join(", ")}`);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          targets.forEach((t) => next.delete(t._id));
          return next;
        });
        handleCloseAssignModal();
      } else {
        const failedEntries = Object.entries(deviceResults);
        const statusMsg = failedEntries.length > 0
          ? failedEntries.map(([sn, status]) => `${sn}: ${status}`).join("; ")
          : serialNumbers.join(", ");
        toast.error(`Profil-Zuweisung fehlgeschlagen: ${statusMsg}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Profil-Zuweisung fehlgeschlagen.");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleContinueSetup = async (reg) => {
    if (!reg?.serialNumber || !reg?.udid) {
      toast.error("Gerät ist noch nicht beim MDM gemeldet. Für ein neues ADE-Profil muss es zurückgesetzt werden.");
      return;
    }
    if (!window.confirm(`Setup-Assistent für ${reg.plannedDeviceName || reg.serialNumber} fortsetzen (DeviceConfigured)?`)) return;
    try {
      await continueConfiguredSetup(reg.serialNumber);
      toast.success("DeviceConfigured gesendet. Das Setup sollte weitergehen.");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Setup konnte nicht fortgesetzt werden.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Registrierung wirklich löschen?")) return;
    try {
      await deviceRegistrationService.deleteDeviceRegistration(id);
      toast.success("Registrierung gelöscht.");
      loadRegistrations();
      if (editingId === id) handleCloseEditModal();
    } catch (err) {
      toast.error("Registrierung konnte nicht gelöscht werden.");
    }
  };

  const handleImport = async () => {
    let parsed;
    try {
      parsed = JSON.parse(importJson);
    } catch {
      toast.error("Ungültiges JSON.");
      return;
    }
    const devices = Array.isArray(parsed) ? parsed : (parsed.devices || []);
    if (devices.length === 0) {
      toast.error('Erwartet: Array von Geräten oder Objekt mit "devices"-Array.');
      return;
    }

    try {
      const result = await deviceRegistrationService.importDeviceRegistrations(devices);
      setImportResult(result);
      toast.success(`Import: ${result.created} angelegt, ${result.updated ?? 0} aktualisiert, ${result.skipped} übersprungen.`);
      loadRegistrations();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Import fehlgeschlagen.");
    }
  };

  const getDepDeviceType = (dep) => {
    if (!dep) return null;
    const s = [dep.model, dep.device_family, dep.os].filter(Boolean).join(" ").toLowerCase();
    if (s.includes("mac")) return "Mac";
    if (s.includes("iphone")) return "iPhone";
    if (s.includes("ipad")) return "iPad";
    if (s.includes("apple tv") || s.includes("appletv")) return "Apple TV";
    return null;
  };

  const getDeviceDetailPath = (reg, dep) => {
    if (reg.enrollmentStatus !== "enrolled") return null;
    const dt = getDepDeviceType(dep);
    const segment = dt && { Mac: "mac", iPhone: "iphone", iPad: "ipad", "Apple TV": "appletv" }[dt];
    return segment ? `/${segment}/${encodeURI(reg.serialNumber)}` : null;
  };

  const getDepProfileStatus = (dep) => {
    if (!dep) return "empty";
    if (dep.profile_status === "pushed") return "pushed";
    if (dep.profile_uuid || dep.profile_status) return "assigned";
    return "empty";
  };

  const filteredRegistrations = useMemo(() => {
    return registrations.filter((reg) => {
      if (locationFilter && (!reg.location?._id || reg.location._id !== locationFilter)) return false;
      if (deviceTypeFilter) {
        const dep = depDevices[reg.serialNumber];
        const dt = getDepDeviceType(dep);
        if (deviceTypeFilter === "nodep") return !dep;
        if (dt !== deviceTypeFilter) return false;
      }
      if (depStatusFilter) {
        const dep = depDevices[reg.serialNumber];
        if (getDepProfileStatus(dep) !== depStatusFilter) return false;
      }
      return true;
    });
  }, [registrations, depDevices, locationFilter, deviceTypeFilter, depStatusFilter]);

  const allFilteredSelected = filteredRegistrations.length > 0 && filteredRegistrations.every((r) => selectedIds.has(r._id));
  const someFilteredSelected = filteredRegistrations.some((r) => selectedIds.has(r._id));
  const selectedRegs = useMemo(() => registrations.filter((r) => selectedIds.has(r._id)), [registrations, selectedIds]);

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredRegistrations.forEach((r) => next.delete(r._id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredRegistrations.forEach((r) => next.add(r._id));
        return next;
      });
    }
  };

  const exampleJson = `[
  { "serialNumber": "G4V2PD977H", "assetTag": "2001086259", "location": "S140", "deviceName": "01_18", "groups": ["AppleTVs"] },
  { "serialNumber": "FYH4KH6TT4", "assetTag": "2001086258", "location": "S140", "deviceName": "03_06", "groups": ["AppleTVs"] },
  { "serialNumber": "CQ3W4MWHQL", "assetTag": "2001086257", "location": "S140", "deviceName": "03_02", "groups": ["AppleTVs"] }
]`;

  if (loading && registrations.length === 0) {
    return <p className="text-center my-4">Laden...</p>;
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
        <h2 className="mb-0">Geräte-Registrierung</h2>
        <div className="d-flex gap-2 align-items-center">
          {isAdministrator() && (
            <Link to="/dep-profiles" className="btn btn-outline-primary btn-sm">DEP-Profile bearbeiten</Link>
          )}
        </div>
      </div>
      <p className="text-muted mb-2">
        Geräte vor dem Enrollment registrieren. Beim ersten Einschreiben werden Location, Name, Asset Tag und die Profile der zugewiesenen Gruppe automatisch angewendet.
      </p>
      <p className="small mb-4">
        {lastAppleSyncLabel
          ? <>Letzter Sync mit Apple: <strong>{lastAppleSyncLabel}</strong></>
          : <span className="text-muted">Noch kein Sync mit Apple aufgezeichnet.</span>}
        {depAccountInfo?.dep_last_sync_source === "manual" ? " (manuell)" : null}
        {depAccountInfo?.dep_last_sync_source === "webhook" ? " (DEP-Syncer)" : null}
      </p>

      {depAccountInfo && (
        <div className="card mb-4 p-3 bg-light">
          <span className="text-muted small">Organisations-Tokens</span>
          <div className="d-flex flex-wrap gap-3 mt-1">
            <span><strong>Server:</strong> {depAccountInfo.server_name}</span>
            <span><strong>Organisation:</strong> {depAccountInfo.org_name}</span>
            {depAccountInfo.facilitator_id && (
              <span><strong>Facilitator:</strong> {depAccountInfo.facilitator_id}</span>
            )}
            {depAccountInfo.admin_id && (
              <span><strong>Admin-ID:</strong> {depAccountInfo.admin_id}</span>
            )}
            {depAccountInfo.server_uuid && (
              <span><strong>Server-UUID:</strong> <code className="small">{depAccountInfo.server_uuid}</code></span>
            )}
            {depAccountInfo.org_email && (
              <span><strong>Org-E-Mail:</strong> {depAccountInfo.org_email}</span>
            )}
            {depAccountInfo.org_phone && (
              <span><strong>Org-Telefon:</strong> {depAccountInfo.org_phone}</span>
            )}
            {depAccountInfo.org_address && (
              <span><strong>Org-Adresse:</strong> {depAccountInfo.org_address}</span>
            )}
            {depAccountInfo.org_type && (
              <span><strong>Org-Typ:</strong> {depAccountInfo.org_type}</span>
            )}
          </div>
          <div className="d-flex flex-wrap gap-3 mt-3 align-items-center">
            {(() => {
              const expiry = depAccountInfo.access_token_expiry || orgTokens?.dep?.accessTokenExpiry;
              const depStatus = orgTokens?.dep?.status;
              const days = orgTokens?.dep?.daysRemaining;
              return (
                <span className={`badge ${expiryBadgeClass(depStatus)}`}>
                  DEP-Token: {expiryLabel(expiry, days)}
                </span>
              );
            })()}
            {orgTokens?.apns && (
              <span className={`badge ${expiryBadgeClass(orgTokens.apns.status)}`}>
                APNs: {expiryLabel(orgTokens.apns.notAfter, orgTokens.apns.daysRemaining)}
              </span>
            )}
            {orgTokens?.profileSigning && (
              <span className={`badge ${expiryBadgeClass(orgTokens.profileSigning.status)}`}>
                Profil-Signatur: {orgTokens.profileSigning.configured
                  ? expiryLabel(orgTokens.profileSigning.notAfter, orgTokens.profileSigning.daysRemaining)
                  : "kein Zertifikat"}
              </span>
            )}
            {orgTokens?.apns?.topic && (
              <span className="small text-muted">
                Topic: <code>{orgTokens.apns.topic}</code>
              </span>
            )}
            {isAdministrator() && (
              <Link to="/settings" className="small">Tokens erneuern</Link>
            )}
          </div>
          {!depAccountInfo.access_token_expiry && !orgTokens?.dep?.accessTokenExpiry && (
            <p className="text-muted small mt-2 mb-0">
              Das DEP-Ablaufdatum konnte nicht gelesen werden. Token in den Einstellungen prüfen.
            </p>
          )}
        </div>
      )}
      {depAccountError && (
        <div className="alert alert-warning mb-4 py-2" role="alert">
          DEP: {depAccountError}
        </div>
      )}
      {!depAccountInfo && orgTokens && (
        <div className="card mb-4 p-3 bg-light">
          <span className="text-muted small">Organisations-Tokens</span>
          <div className="d-flex flex-wrap gap-3 mt-2 align-items-center">
            {orgTokens.apns && (
              <span className={`badge ${expiryBadgeClass(orgTokens.apns.status)}`}>
                APNs: {expiryLabel(orgTokens.apns.notAfter, orgTokens.apns.daysRemaining)}
              </span>
            )}
            {orgTokens.profileSigning && (
              <span className={`badge ${expiryBadgeClass(orgTokens.profileSigning.status)}`}>
                Profil-Signatur: {orgTokens.profileSigning.configured
                  ? expiryLabel(orgTokens.profileSigning.notAfter, orgTokens.profileSigning.daysRemaining)
                  : "kein Zertifikat"}
              </span>
            )}
            {isAdministrator() && (
              <Link to="/settings" className="small">Tokens erneuern</Link>
            )}
          </div>
        </div>
      )}

      <div className="mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="pending">Ausstehend</option>
            <option value="enrolled">Eingeschrieben</option>
            <option value="">Alle</option>
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
          >
            <option value="">Location: Alle</option>
            {locations.map((loc) => (
              <option key={loc._id} value={loc._id}>
                {loc.schoolNumber} – {loc.name}
              </option>
            ))}
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={deviceTypeFilter}
            onChange={(e) => setDeviceTypeFilter(e.target.value)}
          >
            <option value="">Typ: Alle</option>
            <option value="nodep">Nicht in DEP</option>
            <option value="Mac">Mac</option>
            <option value="iPhone">iPhone</option>
            <option value="iPad">iPad</option>
            <option value="Apple TV">Apple TV</option>
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: "auto" }}
            value={depStatusFilter}
            onChange={(e) => setDepStatusFilter(e.target.value)}
          >
            <option value="">DEP-Status: Alle</option>
            <option value="empty">Empty</option>
            <option value="assigned">Assigned</option>
            <option value="pushed">Pushed</option>
          </select>
          {someFilteredSelected && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setAssignTarget(selectedRegs);
                setAssignProfileUuid("");
                setShowAddProfile(false);
                setNewProfileUuid("");
                setNewProfileName("");
                setShowAssignModal(true);
              }}
            >
              DEP-Profil zuweisen ({selectedRegs.length})
            </button>
          )}
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleSyncFromApple}
            disabled={syncingDep}
            title="Geräteliste bei Apple ADE/DEP abfragen"
          >
            <FaSync className={syncingDep ? "spin" : ""} />{" "}
            {syncingDep ? "Sync mit Apple…" : "Von Apple aktualisieren"}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleOpenAddModal}
          >
            Gerät hinzufügen
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => {
              setShowImportModal(true);
              setImportJson("");
              setImportResult(null);
            }}
          >
            JSON Import
          </button>
        </div>
      </div>

      {showImportModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">JSON Import</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportModal(false)}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  Erwartet: Array von Geräten oder Objekt mit &quot;devices&quot;-Array. Felder: serialNumber (Pflicht), deviceName, assetTag, location (schoolNumber, z.B. S140), groups (Gruppennamen oder groupIds; groupId hat Vorrang bei Namenskonflikten), notes.
                </p>
                <textarea
                  className="form-control font-monospace small"
                  rows={10}
                  placeholder={exampleJson}
                  value={importJson}
                  onChange={(e) => setImportJson(e.target.value)}
                />
                {importResult && (
                  <div className="mt-2 p-2 bg-light rounded">
                    <strong>Ergebnis:</strong> {importResult.created} angelegt, {importResult.updated ?? 0} aktualisiert, {importResult.skipped} übersprungen
                    {importResult.errors?.length > 0 && (
                      <pre className="mt-2 mb-0 small text-danger">
                        {JSON.stringify(importResult.errors, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportModal(false)}
                >
                  Schließen
                </button>
                <button type="button" className="btn btn-primary" onClick={handleImport}>
                  Importieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <table className="table">
        <thead>
          <tr>
            <th style={{ width: "40px" }}>
              <input
                type="checkbox"
                className="form-check-input"
                checked={allFilteredSelected}
                ref={(el) => el && (el.indeterminate = someFilteredSelected && !allFilteredSelected)}
                onChange={handleToggleSelectAll}
                title={allFilteredSelected ? "Alle abwählen" : "Alle auswählen"}
              />
            </th>
            <th>Seriennummer</th>
            <th>Geplanter Name</th>
            <th>Asset Tag</th>
            <th>Location</th>
            <th>Gruppen</th>
            <th>Benutzer</th>
            <th>DEP Typ</th>
            <th>DEP Profil</th>
            <th>Status</th>
            <th style={{ width: "140px" }}></th>
          </tr>
        </thead>
        <tbody>
          {filteredRegistrations.map((reg) => {
            const dep = depDevices[reg.serialNumber];
            const isSelected = selectedIds.has(reg._id);
            return (
              <tr key={reg._id} className={isSelected ? "table-active" : ""}>
              <td>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isSelected}
                  onChange={() => handleToggleSelect(reg._id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </td>
              <td>
                {(() => {
                  const path = getDeviceDetailPath(reg, dep);
                  return path ? (
                    <Link to={path}>{reg.serialNumber}</Link>
                  ) : (
                    reg.serialNumber
                  );
                })()}
              </td>
              <td>{reg.plannedDeviceName || "—"}</td>
              <td>{reg.assetTag || "—"}</td>
              <td>{reg.location ? `${reg.location.schoolNumber} – ${reg.location.name}` : "—"}</td>
              <td>{(reg.groups || (reg.group ? [reg.group] : [])).map((g) => g?.name).filter(Boolean).join(", ") || "—"}</td>
              <td>
                {reg.assignedDirectoryUser?.name || "—"}
                {staff ? (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1 align-baseline"
                    onClick={() => setAssignUserTarget(reg)}
                  >
                    zuweisen
                  </button>
                ) : null}
              </td>
              <td title={dep?.model}>{dep ? `${dep.model || dep.device_family || dep.os || "—"}` : "—"}</td>
              <td>
                <div className="d-flex flex-column gap-1">
                  {dep?.profile_status || dep?.profile_uuid ? (
                    dep?.profile_uuid ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className={`badge ${dep.profile_status === 'pushed' ? 'bg-success' : dep.profile_status === 'empty' ? 'bg-secondary' : 'bg-info'} align-self-start`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => dep.profile_status === 'empty' ? handleOpenAssignModal([reg]) : handleShowProfile(dep.profile_uuid)}
                        onKeyDown={(e) => e.key === 'Enter' && (dep.profile_status === 'empty' ? handleOpenAssignModal([reg]) : handleShowProfile(dep.profile_uuid))}
                        title={dep.profile_status === 'empty' ? 'Profil zuweisen' : 'Profil-Details anzeigen'}
                      >
                        {dep.profile_status || dep.profile_uuid}
                      </span>
                    ) : (
                      <span
                        role="button"
                        tabIndex={0}
                        className={`badge ${dep.profile_status === 'pushed' ? 'bg-success' : dep.profile_status === 'empty' ? 'bg-secondary' : 'bg-info'} align-self-start`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => dep.profile_status === 'empty' ? handleOpenAssignModal([reg]) : undefined}
                        onKeyDown={(e) => dep.profile_status === 'empty' && e.key === 'Enter' && handleOpenAssignModal([reg])}
                        title={dep.profile_status === 'empty' ? 'Profil zuweisen' : undefined}
                      >
                        {dep.profile_status}
                      </span>
                    )
                  ) : (
                    <span
                      role="button"
                      tabIndex={0}
                      className="badge bg-secondary align-self-start"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleOpenAssignModal([reg])}
                      onKeyDown={(e) => e.key === 'Enter' && handleOpenAssignModal([reg])}
                      title="Profil zuweisen (Gerät nicht in DEP)"
                    >
                      —
                    </span>
                  )}
                  {dep?.profile_assign_time && (
                    <span className="text-muted small" title="Zugewiesen">
                      {new Date(dep.profile_assign_time).toLocaleString()}
                    </span>
                  )}
                  {dep?.profile_push_time && (
                    <span className="text-muted small" title="Gepusht">
                      {new Date(dep.profile_push_time).toLocaleString()}
                    </span>
                  )}
                </div>
              </td>
              <td>
                <span className={`badge ${reg.enrollmentStatus === 'enrolled' ? 'bg-success' : 'bg-secondary'}`}>
                  {reg.enrollmentStatus}
                </span>
              </td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary me-1"
                  onClick={() => handleOpenAssignModal([reg])}
                >
                  Profil zuweisen
                </button>
                <button
                  className="btn btn-sm btn-outline-primary me-1"
                  onClick={() => handleOpenEditModal(reg)}
                >
                  Bearbeiten
                </button>
                {reg.udid && (
                  <button
                    className="btn btn-sm btn-outline-success me-1"
                    onClick={() => handleContinueSetup(reg)}
                    title="Sendet DeviceConfigured, falls das Gerät im Setup auf die Vorschaltseite wartet"
                  >
                    Setup fortsetzen
                  </button>
                )}
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(reg._id)}
                >
                  Löschen
                </button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>

      {filteredRegistrations.length === 0 && (
        <p className="text-muted">
          {registrations.length === 0 ? "Keine Registrierungen in dieser Kategorie." : "Keine Einträge passen zu den gewählten Filtern."}
        </p>
      )}

      {showEditModal && editTarget && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Gerät bearbeiten</h5>
                  <button type="button" className="btn-close" onClick={handleCloseEditModal} />
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    Seriennummer: <strong>{editTarget.serialNumber}</strong>
                  </p>
                  <div className="row g-2 mb-2">
                    <div className="col-12">
                      <label className="form-label">Geplanter Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="MacBook-S15-01"
                        value={formData.plannedDeviceName}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, plannedDeviceName: e.target.value }))
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Asset Tag</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="AT-001"
                        value={formData.assetTag}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, assetTag: e.target.value }))
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Location</label>
                      <select
                        className="form-select"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, location: e.target.value }))
                        }
                      >
                        <option value="">—</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc._id}>
                            {loc.schoolNumber} – {loc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Gruppen</label>
                      <div className="border rounded p-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                        {groups.map((grp) => (
                          <div key={grp._id} className="form-check">
                            <input
                              type="checkbox"
                              id={`edit-group-${grp._id}`}
                              className="form-check-input"
                              checked={formData.groups.includes(grp._id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData((prev) => ({
                                  ...prev,
                                  groups: checked ? [...prev.groups, grp._id] : prev.groups.filter((id) => id !== grp._id),
                                }));
                              }}
                            />
                            <label className="form-check-label" htmlFor={`edit-group-${grp._id}`}>
                              {grp.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notizen</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, notes: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseEditModal}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Aktualisieren
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <form onSubmit={handleSubmit}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Gerät hinzufügen</h5>
                  <button type="button" className="btn-close" onClick={handleCloseAddModal} />
                </div>
                <div className="modal-body">
                  <div className="row g-2 mb-2">
                    <div className="col-12">
                      <label className="form-label">Seriennummer *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="C02XY1234"
                        value={formData.serialNumber}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, serialNumber: e.target.value }))
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Geplanter Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="MacBook-S15-01"
                        value={formData.plannedDeviceName}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, plannedDeviceName: e.target.value }))
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Asset Tag</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="AT-001"
                        value={formData.assetTag}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, assetTag: e.target.value }))
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Location</label>
                      <select
                        className="form-select"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, location: e.target.value }))
                        }
                      >
                        <option value="">—</option>
                        {locations.map((loc) => (
                          <option key={loc._id} value={loc._id}>
                            {loc.schoolNumber} – {loc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Gruppen</label>
                      <div className="border rounded p-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                        {groups.map((grp) => (
                          <div key={grp._id} className="form-check">
                            <input
                              type="checkbox"
                              id={`add-group-${grp._id}`}
                              className="form-check-input"
                              checked={formData.groups.includes(grp._id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData((prev) => ({
                                  ...prev,
                                  groups: checked ? [...prev.groups, grp._id] : prev.groups.filter((id) => id !== grp._id),
                                }));
                              }}
                            />
                            <label className="form-check-label" htmlFor={`add-group-${grp._id}`}>
                              {grp.name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notizen</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, notes: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseAddModal}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Hinzufügen
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssignModal && assignTarget && assignTarget.length > 0 && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">DEP-Profil zuweisen</h5>
                <button type="button" className="btn-close" onClick={handleCloseAssignModal} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  {assignTarget.length === 1 ? (
                    <>Gerät: <strong>{assignTarget[0].serialNumber}</strong> {assignTarget[0].plannedDeviceName && `(${assignTarget[0].plannedDeviceName})`}</>
                  ) : (
                    <><strong>{assignTarget.length} Geräte</strong> ausgewählt: {assignTarget.map((t) => t.serialNumber).join(", ")}</>
                  )}
                </p>
                <div className="mb-3">
                  <label className="form-label">Profil</label>
                  <select
                    className="form-select"
                    value={assignProfileUuid}
                    onChange={(e) => setAssignProfileUuid(e.target.value)}
                  >
                    <option value="">— Bitte wählen —</option>
                    {depProfiles.map((p) => (
                      <option key={p.profile_uuid} value={p.profile_uuid}>
                        {p.profile_name || p.profile_uuid}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="small mb-2">
                  <Link to="/dep-profiles">Neues Profil im Editor anlegen</Link>
                  {" "}oder eine bestehende Apple-UUID importieren:
                </p>
                <details className="small">
                  <summary className="text-muted" style={{ cursor: "pointer" }}>Bestehende UUID importieren</summary>
                  <div className="mt-2 p-2 border rounded">
                    <div className="mb-2">
                      <label className="form-label small mb-0">Profile UUID *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        placeholder="5FE39F1E78DDA93563826DAA5B53B93C"
                        value={newProfileUuid}
                        onChange={(e) => setNewProfileUuid(e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small mb-0">Name (optional)</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Mein DEP-Profil"
                        value={newProfileName}
                        onChange={(e) => setNewProfileName(e.target.value)}
                      />
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleAddDepProfile}>
                      Hinzufügen
                    </button>
                  </div>
                </details>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseAssignModal}>
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!assignProfileUuid || assignLoading}
                  onClick={handleAssignProfile}
                >
                  {assignLoading ? "Zuweisen…" : "Zuweisen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">DEP-Profil Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseProfileModal}
                />
              </div>
              <div className="modal-body">
                {profileLoading && <p className="text-muted">Laden…</p>}
                {profileError && <div className="alert alert-danger">{profileError}</div>}
                {profileData && !profileLoading && (
                  <div className="small">
                    <dl className="row mb-0">
                      {profileData.profile_name && (
                        <><dt className="col-sm-4">Profil-Name</dt><dd className="col-sm-8">{profileData.profile_name}</dd></>
                      )}
                      {profileData.profile_uuid && (
                        <><dt className="col-sm-4">Profile UUID</dt><dd className="col-sm-8 font-monospace">{profileData.profile_uuid}</dd></>
                      )}
                      {profileData.support_phone_number && (
                        <><dt className="col-sm-4">Support-Telefon</dt><dd className="col-sm-8">{profileData.support_phone_number}</dd></>
                      )}
                      {profileData.support_email_address && (
                        <><dt className="col-sm-4">Support-E-Mail</dt><dd className="col-sm-8">{profileData.support_email_address}</dd></>
                      )}
                      {profileData.department && (
                        <><dt className="col-sm-4">Abteilung</dt><dd className="col-sm-8">{profileData.department}</dd></>
                      )}
                      {profileData.url && (
                        <><dt className="col-sm-4">URL</dt><dd className="col-sm-8"><a href={profileData.url} target="_blank" rel="noopener noreferrer">{profileData.url}</a></dd></>
                      )}
                      {profileData.org_magic && (
                        <><dt className="col-sm-4">Org Magic</dt><dd className="col-sm-8 font-monospace small">{profileData.org_magic}</dd></>
                      )}
                      {profileData.language && (
                        <><dt className="col-sm-4">Sprache</dt><dd className="col-sm-8">{profileData.language}</dd></>
                      )}
                      {profileData.region && (
                        <><dt className="col-sm-4">Region</dt><dd className="col-sm-8">{profileData.region}</dd></>
                      )}
                      <dt className="col-sm-4">Supervised</dt><dd className="col-sm-8">{profileData.is_supervised ? "Ja" : "Nein"}</dd>
                      <dt className="col-sm-4">Pairing erlaubt</dt><dd className="col-sm-8">{profileData.allow_pairing ? "Ja" : "Nein"}</dd>
                      <dt className="col-sm-4">Pflichtprofil</dt><dd className="col-sm-8">{profileData.is_mandatory ? "Ja" : "Nein"}</dd>
                      <dt className="col-sm-4">MDM entfernbar</dt><dd className="col-sm-8">{profileData.is_mdm_removable ? "Ja" : "Nein"}</dd>
                      {profileData.skip_setup_items?.length > 0 && (
                        <><dt className="col-sm-4">Setup-Schritte übersprungen</dt><dd className="col-sm-8"><span className="text-muted">{profileData.skip_setup_items.length} Einträge</span></dd></>
                      )}
                    </dl>
                    {profileData.skip_setup_items?.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-muted small">Skip-Setup-Items anzeigen</summary>
                        <ul className="small mt-1 mb-0">
                          {profileData.skip_setup_items.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseProfileModal}>
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <AssignUserModal
        visible={Boolean(assignUserTarget)}
        registrationId={assignUserTarget?._id}
        udid={assignUserTarget?.udid}
        serialNumber={assignUserTarget?.serialNumber}
        currentUser={assignUserTarget?.assignedDirectoryUser}
        onClose={() => setAssignUserTarget(null)}
        onSaved={() => {
          setAssignUserTarget(null);
          loadRegistrations();
        }}
      />
    </div>
  );
};

export default DeviceRegistrations;
